import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { WhipLogo } from "@/components/WhipLogo";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-bold text-white mb-2">{title}</h2>
    <div className="text-sm text-white/60 leading-relaxed space-y-2">{children}</div>
  </section>
);

const DeletionRequestCard = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/account/deletion-request`)
      .then((r) => setStatus(r.data?.status || null))
      .catch(() => {});
  }, [user]);

  if (!user) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="deletion-signin-note">
        <p className="text-sm text-white/60">
          To request deletion of your account and associated data, sign in first and return to this page.
          If you can no longer access your account, contact your WHIP administrator or reply to your
          invitation email and we will process the request manually.
        </p>
        <Link to="/login" className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-orange-400 hover:text-orange-300" data-testid="deletion-signin-link">
          Sign in <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
        </Link>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5 flex items-start gap-3" data-testid="deletion-pending">
        <CheckCircle2 className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
        <p className="text-sm text-amber-200/80">
          Your deletion request is pending review. An administrator will remove your account and
          associated personal data. You'll lose access once it's processed.
        </p>
      </div>
    );
  }

  const submit = async () => {
    if (!window.confirm("Request permanent deletion of your WHIP account and personal data?")) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/account/deletion-request`, { reason: "" });
      setStatus(r.data?.status || "pending");
      toast.success("Deletion request submitted");
    } catch {
      toast.error("Could not submit the request, please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="deletion-request-card">
      <p className="text-sm text-white/60 mb-3">
        Signed in as <span className="text-white font-mono">{user.email}</span>. Submitting this request
        queues your account for permanent deletion by an administrator, including your profile, saved
        watchlists, and usage records.
      </p>
      <button
        onClick={submit}
        disabled={submitting}
        className="inline-flex items-center gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-300 hover:bg-red-500/20 transition-colors disabled:opacity-50"
        data-testid="deletion-request-button"
      >
        <Trash2 className="w-4 h-4" /> Request account deletion
      </button>
    </div>
  );
};

export default function Privacy() {
  return (
    <div className="min-h-screen" style={{ background: "#0b0b0f" }}>
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/about" data-testid="privacy-logo-link"><WhipLogo size={24} /></Link>
          <Link to="/about" className="inline-flex items-center gap-1.5 text-[13px] text-white/60 hover:text-white" data-testid="privacy-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="privacy-page">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-5 h-5 text-orange-400" />
          <p className="text-[11px] font-mono uppercase tracking-widest text-orange-400/80">Privacy Notice</p>
        </div>
        <h1 className="text-4xl sm:text-5xl font-black text-white mb-3">How WHIP handles your data</h1>
        <p className="text-sm text-white/40 mb-10 font-mono">Last updated: June 2026</p>

        <Section title="What we collect">
          <p><span className="text-white/85 font-semibold">Account information</span>, your name, email address, and a securely hashed password (we never store your password itself). If you sign in with Google, we receive your name, email, and profile picture from Google.</p>
          <p><span className="text-white/85 font-semibold">Professional information</span>, if you request early access, the organization, role, audience category, and intended use you choose to share.</p>
          <p><span className="text-white/85 font-semibold">Product usage</span>, which WHIP features you view (for example, forecasts or rankings), recorded with your account ID and subscription tier. These records automatically expire after 90 days.</p>
          <p><span className="text-white/85 font-semibold">Security records</span>, failed sign-in counters used for lockout protection, and administrative change logs.</p>
        </Section>

        <Section title="What we don't collect">
          <p>We do not collect payment card data, precise location, contacts, or browsing activity outside WHIP. Usage analytics never contain passwords, authentication tokens, or message content.</p>
        </Section>

        <Section title="How it's used">
          <p>Your data is used to operate your account, enforce your subscription's access level, keep the platform secure, and understand which intelligence features are most valuable so we can improve them. We do not sell personal information.</p>
        </Section>

        <Section title="Who can see it">
          <p>WHIP administrators can view account and waitlist details to manage access. If you belong to a team workspace, your organization's members can see watchlist items you explicitly share with the organization, nothing else.</p>
        </Section>

        <Section title="The legislative data itself">
          <p>All congressional intelligence in WHIP is built from official public records (Congress.gov, House and Senate roll-call publications) and public financial-disclosure data about legislators, not about you.</p>
        </Section>

        <Section title="Delete your account">
          <DeletionRequestCard />
        </Section>

        <p className="text-[11px] text-white/30 leading-relaxed">
          This notice describes WHIP's current practices in plain language and will be reviewed by counsel
          before general availability. Questions or data requests: contact your WHIP administrator.
        </p>
      </main>

      <footer className="border-t border-white/10 py-6">
        <p className="text-center text-[11px] font-mono text-white/30">
          WHIP · Built on official public congressional records
        </p>
      </footer>
    </div>
  );
}
